import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Crown, Calendar, Infinity as InfinityIcon, ExternalLink, LogOut, RefreshCw } from "lucide-react";
import { useAuth, isPlanActive } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LINK_ANUAL = process.env.REACT_APP_HOTMART_ANUAL || "https://pay.hotmart.com/PRODUTO_ANUAL";
const LINK_VITALICIO = process.env.REACT_APP_HOTMART_VITALICIO || "https://pay.hotmart.com/PRODUTO_VITALICIO";

export default function PaywallModal({ open, onOpenChange }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);

  const doLogout = async () => {
    await auth.logout();
    onOpenChange(false);
    navigate("/login");
  };

  const verificarAcesso = async () => {
    setChecking(true);
    try {
      try { await axios.post(`${API}/auth/restore-access`); } catch { /* ignore */ }
      try { await axios.post(`${API}/auth/sync-plan`); } catch { /* ignore */ }
      const updated = await auth.refreshUser();
      if (isPlanActive(updated)) {
        toast.success("Acesso liberado! Bem-vindo ao Monyxx.");
        onOpenChange(false);
        navigate("/app");
      } else {
        toast.info("Pagamento ainda não confirmado. Se acabou de pagar, aguarde 1 minuto e tente novamente.");
      }
    } catch {
      toast.error("Erro ao verificar. Tente novamente.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden border-0" data-testid="paywall-modal" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <div className="bg-gradient-to-br from-emerald-600 via-blue-700 to-blue-900 text-white p-8 relative">
          <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-emerald-300/15 blur-2xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[11px] font-bold tracking-wide">
              <Crown className="h-3.5 w-3.5" /> PLANO NECESSÁRIO
            </div>
            <DialogHeader className="mt-3">
              <DialogTitle className="font-display text-3xl text-white">
                Continue cuidando do seu dinheiro
              </DialogTitle>
              <DialogDescription className="text-emerald-50 mt-2">
                Escolha um plano e libere o acesso completo agora mesmo.
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <div className="p-6 bg-slate-950 space-y-3">
          <a href={LINK_VITALICIO} target="_blank" rel="noopener noreferrer" data-testid="cta-vitalicio"
             className="block rounded-2xl p-5 bg-gradient-to-br from-emerald-500 to-emerald-700 text-white hover:scale-[1.02] transition-transform">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <InfinityIcon className="h-6 w-6" />
                <div>
                  <div className="font-display text-xl font-bold">Plano Vitalício</div>
                  <div className="text-xs opacity-90">Pague uma vez, use para sempre</div>
                </div>
              </div>
              <ExternalLink className="h-5 w-5 opacity-80" />
            </div>
          </a>

          <a href={LINK_ANUAL} target="_blank" rel="noopener noreferrer" data-testid="cta-anual"
             className="block rounded-2xl p-5 bg-slate-900 border border-slate-800 hover:border-blue-500/40 transition">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="h-6 w-6 text-blue-300" />
                <div>
                  <div className="font-display text-xl font-bold text-white">Plano Anual</div>
                  <div className="text-xs text-slate-400">Renovação anual · cancele quando quiser</div>
                </div>
              </div>
              <ExternalLink className="h-5 w-5 text-slate-400" />
            </div>
          </a>

          <p className="text-xs text-slate-500 text-center pt-1">
            Use o mesmo e-mail do cadastro: <b className="text-emerald-300">{auth?.user?.email}</b>
          </p>

          {/* Já paguei — verificar */}
          <Button
            onClick={verificarAcesso}
            disabled={checking}
            variant="outline"
            className="w-full border-emerald-700 text-emerald-400 hover:bg-emerald-900/30"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${checking ? "animate-spin" : ""}`} />
            {checking ? "Verificando..." : "Já paguei — verificar acesso"}
          </Button>

          <Button onClick={doLogout} variant="ghost" className="w-full text-slate-400 hover:text-white" data-testid="paywall-logout">
            <LogOut className="h-4 w-4 mr-1.5" /> Sair da conta
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
